// Shared navigation data for the Citizens' Gavel landing page.
// Focus-area, resource and about icons are exported from Figma and live in
// /public/landing/icons/menu/. Innovation entries use the real product marks
// from @podus/ui/logo-icons instead, so the menu shows the logo each product
// actually ships with.

import harmlistLogo from '@podus/ui/logo-icons/harmlist.png';
import irisLogo from '@podus/ui/logo-icons/iris.png';
import justiceClockLogo from '@podus/ui/logo-icons/justice-clock.png';
import openJusticeLogo from '@podus/ui/logo-icons/open-justice.png';
import podusAidLogo from '@podus/ui/logo-icons/podus-aid.png';
import podusAiLogo from '@podus/ui/logo-icons/podus-ai.png';
import podusAppointmentLogo from '@podus/ui/logo-icons/podus-appointment.png';
import podusContractLogo from '@podus/ui/logo-icons/podus-contract.png';
import podusMultidoorLogo from '@podus/ui/logo-icons/podus-multidoor.png';
import yourcheckaLogo from '@podus/ui/logo-icons/yourcheaka.png';

const ICON = '/landing/icons/menu';

export interface MenuItem {
    label: string;
    icon: string;
    description: string;
    href: string;
    /** Focus areas only: position in the homepage "Our Programmes" stack, from the CMS. 0 = not shown. */
    homeSlot?: number;
}

export type MenuKey = 'about' | 'focus' | 'resources' | 'innovations';

export interface MegaMenuData {
    key: MenuKey;
    label: string;
    title: string;
    columns: number;
    items: MenuItem[];
}

export const FOCUS_AREAS: MenuItem[] = [
    {
        label: 'Tech For Justice',
        icon: `${ICON}/focus-tech-for-justice.svg`,
        description: 'Building the tools the justice system never built for itself.',
        href: '/focus-areas/tech-for-justice',
    },
    {
        label: 'Access To Justice',
        icon: `${ICON}/focus-access-to-justice.svg`,
        description: 'Free representation for people who cannot buy their way to a lawyer.',
        href: '/focus-areas/access-to-justice',
    },
    {
        label: 'Judicial Accountability',
        icon: `${ICON}/focus-judicial-accountability.svg`,
        description: 'Measuring how long courts take — and publishing what we find.',
        href: '/focus-areas/judicial-accountability',
    },
    {
        label: 'Democratic Governance',
        icon: `${ICON}/focus-democratic-governance.webp`,
        description: 'Holding public power answerable in practice, not just on paper.',
        href: '/focus-areas/democratic-governance',
    },
    {
        label: 'Sexual Gender Based Violence',
        icon: `${ICON}/focus-sgbv.svg`,
        description: 'Survivor-led support, from the first report through to trial.',
        href: '/focus-areas/sgbv',
    },
];

export const RESOURCES: MenuItem[] = [
    {
        label: 'Blog',
        icon: `${ICON}/resources-blog.svg`,
        description: 'Field notes and analysis from the work of opening up justice.',
        href: '/resources/blog',
    },
    {
        label: 'Publications',
        icon: `${ICON}/resources-publications.svg`,
        description: 'Research, policy briefs and case studies — free to download.',
        href: '/resources/publications',
    },
    {
        label: 'Stories',
        icon: `${ICON}/resources-stories.svg`,
        description: 'How access to justice changed real lives, one case at a time.',
        href: '/resources/stories',
    },
    {
        label: 'Gallery',
        icon: `${ICON}/resources-gallery.svg`,
        description: 'Films and photographs from the clinics, launches and courtrooms.',
        href: '/resources/gallery',
    },
    {
        label: 'High Court Ranking',
        icon: `${ICON}/resources-high-court-ranking.svg`,
        description: 'How every state high court performs, year by year.',
        href: '/resources/court-ranking',
    },
    {
        label: 'Infographics',
        icon: `${ICON}/resources-infographics.svg`,
        description: 'The numbers behind the work, drawn out and ready to share.',
        href: '/resources/infographics',
    },
    {
        label: 'Reports',
        icon: `${ICON}/resources-reports.svg`,
        description: 'Annual reviews, impact reports and monitoring findings.',
        href: '/resources/reports',
    },
];

export const ABOUT_US: MenuItem[] = [
    {
        label: 'Who We Are',
        icon: `${ICON}/about-who-we-are.svg`,
        description: 'Why we started, and the single test we hold our work to.',
        href: '/about#who-we-are',
    },
    {
        label: 'Projects',
        icon: `${ICON}/about-projects.svg`,
        description: 'Everything we run, grouped by the programme it belongs to.',
        href: '/projects',
    },
    {
        label: 'Our Team',
        icon: `${ICON}/about-our-team.svg`,
        description: 'The lawyers, researchers and engineers behind the work.',
        href: '/about#team',
    },
    {
        label: 'Volunteer',
        icon: `${ICON}/about-volunteer.svg`,
        description: 'Give a few hours a month — no law degree required.',
        href: '/about#volunteers',
    },
    {
        label: 'Partnerships',
        icon: `${ICON}/about-partnerships.svg`,
        description: 'Courts, funders and civic partners we build alongside.',
        href: '/about#partners',
    },
];

export const INNOVATIONS: MenuItem[] = [
    {
        label: 'IRIS',
        icon: irisLogo,
        description: 'Screening that spots intimate-partner risk before it escalates.',
        href: '/innovations/iris',
    },
    {
        label: 'Justice Clock',
        icon: justiceClockLogo,
        description: 'Starts the moment a case is filed, and never stops counting.',
        href: '/innovations/justice-clock',
    },
    {
        label: 'Judicial Accountability',
        icon: openJusticeLogo,
        description: 'Court performance data, open to citizens and judges alike.',
        href: '/innovations/judicial-accountability',
    },
    {
        label: 'Podus AI',
        icon: podusAiLogo,
        description: 'Plain-language answers to legal questions, free to start.',
        href: '/innovations/podus-ai',
    },
    {
        label: 'Podus Multidoor Court',
        icon: podusMultidoorLogo,
        description: 'Mediation and arbitration that settle it without a courtroom.',
        href: '/innovations/podus-multidoor-court',
    },
    {
        label: 'Podus Appointment',
        icon: podusAppointmentLogo,
        description: 'Book a verified lawyer for a time that actually suits you.',
        href: '/innovations/podus-appointment',
    },
    {
        label: 'Podus Checker',
        icon: yourcheckaLogo,
        description: 'Verify a lawyer, a company or a document before you commit.',
        href: '/innovations/podus-checker',
    },
    {
        label: 'Podus Contract',
        icon: podusContractLogo,
        description: 'Draft, review and sign agreements you can actually read.',
        href: '/innovations/podus-contract',
    },
    {
        label: 'Podus Legal Aid',
        icon: podusAidLogo,
        description: 'Tell us what happened; we match you with a lawyer who can act.',
        href: '/innovations/podus-legal-aid',
    },
    {
        label: 'Harmlist',
        icon: harmlistLogo,
        description: 'Check a loan app or a company before it costs you something.',
        href: '/innovations/harmlist',
    },
];

/**
 * Social destinations, used by the footer and every social band. The CMS owns
 * these; an empty string means the icon links nowhere rather than to a 404.
 */
/**
 * The organisation's accounts, as the previous site linked to them — the handles
 * are `citizen_gavel` / `CitizenGavel` / `citizens_gavel`, not the `citizensgavel`
 * this file guessed at during the rebuild (`x.com/CitizensGavel` was a 404).
 * A network with no account is left empty and simply is not shown; the CMS
 * overwrites all of this on load.
 */
export const SOCIAL_URLS: Record<string, string> = {
    x: 'https://x.com/citizen_gavel',
    facebook: 'https://www.facebook.com/CitizenGavel',
    instagram: 'https://www.instagram.com/citizens_gavel/',
    linkedin: 'https://www.linkedin.com/company/citizengavel/',
    youtube: 'https://www.youtube.com/channel/UC6dqOqT8Dw9HFh8cGXsUHSw',
    tiktok: '',
};

/**
 * Site-wide links the CMS owns. `donate` is the Flutterwave page the old site
 * linked to; the header hides the button when the CMS leaves it empty.
 */
export const SITE_LINKS = {
    donate: 'https://dashboard.flutterwave.com/donate/uxkfuzymmpkj',
};

export const MEGA_MENUS: Record<MenuKey, MegaMenuData> = {
    focus: { key: 'focus', label: 'Focus Area', title: 'Our Focus Areas', columns: 3, items: FOCUS_AREAS },
    resources: { key: 'resources', label: 'Resources', title: 'Resources', columns: 3, items: RESOURCES },
    about: { key: 'about', label: 'About Us', title: 'About Us', columns: 3, items: ABOUT_US },
    innovations: { key: 'innovations', label: 'Innovations', title: 'Innovations', columns: 4, items: INNOVATIONS },
};

export interface NavLink {
    label: string;
    menu?: MenuKey;
    href?: string;
    chevron?: boolean;
}

// Order as shown in the Figma navbar. In the Figma hero frame, "About Us" renders
// without a chevron (it still opens its mega-menu on hover); the other three show a chevron.
/**
 * The routes each dropdown covers, so the bar can show which section you are
 * reading. Anchor links (`/about#team`) belong to their page, so only the path
 * matters here.
 */
const MENU_ROUTES: Record<MenuKey, string[]> = {
    about: ['/about', '/projects'],
    focus: ['/focus-areas'],
    resources: ['/resources'],
    innovations: ['/innovations', '/our-solutions'],
};

/** Whether a top-level nav entry is the one currently being read. */
export function isCurrentNavLink(link: NavLink, pathname: string): boolean {
    const covers = (route: string): boolean => pathname === route || pathname.startsWith(`${route}/`);
    if (link.menu) return MENU_ROUTES[link.menu].some(covers);
    const href = (link.href ?? '/').split('#')[0];
    // Home would otherwise match every path.
    return href === '/' ? pathname === '/' : covers(href);
}

/**
 * Whether a dropdown entry points at what you are reading. Entries that carry a
 * fragment (`/about#team`) only count when that fragment is the one in the bar —
 * otherwise every About entry would light up together on `/about`.
 */
export function isCurrentMenuItem(href: string, pathname: string, hash = ''): boolean {
    const [path, fragment] = href.split('#');
    const base = path || '/';
    const onPath = base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);
    if (!onPath) return false;
    return fragment ? hash === `#${fragment}` : true;
}

export const NAV_LINKS: NavLink[] = [
    { label: 'Home', href: '/' },
    { label: 'About Us', menu: 'about', chevron: true },
    { label: 'Focus Area', menu: 'focus', chevron: true },
    { label: 'Resources', menu: 'resources', chevron: true },
    { label: 'Innovations', menu: 'innovations', chevron: true },
    { label: 'Help', href: '/help' },
];
